import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { parseCalendarDay } from '../lib/intl.js';
import styles from './DateCalendar.module.css';

/**
 * The month grid a `date` question is answered on (PLAN.md Phase 10.16): a
 * month name, weekday initials, and the days of that month, with the chosen one
 * ringed — the calendar the design was drawn from.
 *
 * Deals only in `YYYY-MM-DD` strings, never `Date` objects, at its boundary.
 * That is the whole discipline that keeps a guess honest: the day rendered is
 * the day stored is the day scored, with no time zone anywhere to shift it (see
 * `question_predictions.dateGuess`).
 *
 * Which month it opens on is the organizer's `dateTargetMonth`, since the point
 * of one of these pools is a shared hunch about one specific window — but
 * paging is never restricted, because a guess a month either side is still a
 * legitimate guess.
 */
export default function DateCalendar({
  value,
  targetMonth,
  disabled = false,
  onPick,
}: {
  /** The currently chosen day, `YYYY-MM-DD`, or null if unanswered. */
  value: string | null;
  /** `YYYY-MM-DD` (any day in it) — which month to open on. Null means the current month. */
  targetMonth: string | null;
  disabled?: boolean;
  onPick: (day: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';

  // Opens on the answer if there is one, else the organizer's target month,
  // else today — so returning to a question you've answered lands on your own
  // guess rather than making you find it again.
  const [visibleMonth, setVisibleMonth] = useState<{ year: number; month: number }>(() => {
    const anchor =
      parseCalendarDay(value ?? '') ?? parseCalendarDay(targetMonth ?? '') ?? new Date();
    return { year: anchor.getFullYear(), month: anchor.getMonth() };
  });

  const { monthLabel, weekdayLabels, leadingBlanks, daysInMonth } = useMemo(() => {
    const first = new Date(visibleMonth.year, visibleMonth.month, 1);

    // Monday-first, matching the design's "M T W T F S S" header. `getDay()` is
    // Sunday-first, so shift it.
    const mondayFirstIndex = (first.getDay() + 6) % 7;

    const weekdayFormat = new Intl.DateTimeFormat(locale, { weekday: 'narrow' });
    // Any known Monday works as a seed for the seven labels; 2024-01-01 was one.
    const labels = Array.from({ length: 7 }, (_, i) =>
      weekdayFormat.format(new Date(2024, 0, 1 + i)),
    );

    return {
      // Month AND year: a birth-date pool routinely spans a year boundary
      // (guessing late December from November), and "DECEMBER" alone doesn't
      // say which one you are about to commit to.
      monthLabel: new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(first),
      weekdayLabels: labels,
      leadingBlanks: mondayFirstIndex,
      daysInMonth: new Date(visibleMonth.year, visibleMonth.month + 1, 0).getDate(),
    };
  }, [visibleMonth, locale]);

  const shiftMonth = (delta: number) => {
    setVisibleMonth((current) => {
      const shifted = new Date(current.year, current.month + delta, 1);
      return { year: shifted.getFullYear(), month: shifted.getMonth() };
    });
  };

  /** `YYYY-MM-DD` for a day of the visible month, assembled by hand rather than via `toISOString()` (which would shift it into UTC). */
  const dayKey = (day: number) =>
    `${visibleMonth.year}-${String(visibleMonth.month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

  return (
    <div className={styles.calendar}>
      <div className={styles.header}>
        <button
          type="button"
          className={styles.monthNav}
          aria-label={t('customCompetition.dateCalendarPreviousMonth')}
          onClick={() => shiftMonth(-1)}
        >
          ‹
        </button>
        <span className={styles.monthLabel}>{monthLabel}</span>
        <button
          type="button"
          className={styles.monthNav}
          aria-label={t('customCompetition.dateCalendarNextMonth')}
          onClick={() => shiftMonth(1)}
        >
          ›
        </button>
      </div>

      <div className={styles.grid} role="grid">
        {weekdayLabels.map((label, index) => (
          // Weekday initials repeat across a week ("T" twice), so the index is
          // the only stable key here.
          <span key={index} className={styles.weekday} aria-hidden="true">
            {label}
          </span>
        ))}

        {Array.from({ length: leadingBlanks }, (_, i) => (
          <span key={`blank-${i}`} className={styles.blank} />
        ))}

        {Array.from({ length: daysInMonth }, (_, i) => {
          const day = i + 1;
          const key = dayKey(day);
          const selected = value === key;

          return (
            <button
              key={key}
              type="button"
              className={`${styles.day} ${selected ? styles.daySelected : ''}`}
              // The visible number alone reads as "12" out of context; the
              // full date is what a screen reader should announce.
              aria-label={new Intl.DateTimeFormat(locale, {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              }).format(new Date(visibleMonth.year, visibleMonth.month, day))}
              aria-pressed={selected}
              disabled={disabled}
              onClick={() => onPick(key)}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
}
